import {z} from 'zod';
import {stringToJSONSchema} from '@netx/core-schema';

/*
// server response
{
    blocks: {
        assetIds: [],
        goo: "{\\\"type\\\":\\\"categoryAssetList\\\",\\\"html\\\":\\\"\\\",\\\"contentIds\\\":[\\\"7\\\"],\\\"contentLabels\\\":[\\\"Images\\\"],\\\"buttonDownload\\\":false,\\\"buttonCart\\\":false,\\\"linkUrl\\\":\\\"\\\",\\\"linkTitle\\\":\\\"\\\",\\\"imageSize\\\":\\\"thumb\\\",\\\"itemImageSize\\\":\\\"preview\\\",\\\"displayAssetName\\\":\\\"0\\\",\\\"listTitle\\\":\\\"\\\",\\\"listButtonDownload\\\":true,\\\"listButtonCart\\\":true,\\\"displayListItemName\\\":true,\\\"ordinal\\\":0}",
        id: 1,
        modDate: "2023-03-02 12:53:19",
        ordinal: 1,
        userId: 27
    }[],
    goo: "{\\\"template\\\":\\\"template1\\\",\\\"title\\\":\\\"New page\\\",\\\"displayTitle\\\":true}",
    id: 1,
    modDate: "2023-03-02 12:53:19",
    portalId: 1,
    slug: "new-page",
    userId: 27
}[]
*/

export enum BrandPortalPageBlockDisplayAssetName {
	NONE = '0',
	ABOVE_IMAGE = '1',
	BELOW_IMAGE = '2',
}

export enum BrandPortalPageBlockImageSize {
	THUMB = 'thumb',
	PREVIEW = 'preview',
	ORIGINAL = 'original',
}

export enum BrandPortalPageBlockType {
	ASSET = 'asset',
	CAROUSEL = 'carousel',
	CATEGORY = 'category',
	CATEGORY_ASSET_LIST = 'categoryAssetList',
	DIVIDER = 'divider',
	HTML = 'html',
	SAVED_CART = 'savedCart',
}

export const BRAND_PORTAL_PAGE_BLOCK_GOO_KEYS = [
	'type',
	'html',
	'contentIds',
	'contentLabels',
	'buttonDownload',
	'buttonCart',
	'linkUrl',
	'linkTitle',
	'imageSize',
	'itemImageSize',
	'displayAssetName',
	'listTitle',
	'listButtonDownload',
	'listButtonCart',
	'displayListItemName',
	'ordinal',
] as const;

export const BrandPortalPageBlockSchema = z.object({
	id: z.number().int().positive(),
	assetIds: z.array(z.number().int().positive()),
	goo: stringToJSONSchema.pipe(
		z.object({
			type: z.nativeEnum(BrandPortalPageBlockType),
			html: z.string(),
			/**
			 * if `type` is categoryAssetList, this is the category id\
			 * if `type` is carousel, this is the category id\
			 * if `type` is asset, this is the asset id\
			 * if `type` is savedCart, this is the saved cart id\
			 */
			contentIds: z.array(z.string()), // TODO: are we going to parse this to number[]?
			contentLabels: z.array(z.string()),
			buttonDownload: z.boolean(),
			buttonCart: z.boolean(),
			linkUrl: z.string(),
			linkTitle: z.string(),
			/** BrandPortalPageBlockType.ASSET block type image size */
			imageSize: z.nativeEnum(BrandPortalPageBlockImageSize),
			/** BrandPortalPageBlockType.CATEGORY_ASSET_LIST block type image sizes */
			itemImageSize: z.nativeEnum(BrandPortalPageBlockImageSize),
			displayAssetName: z.string(), // TODO: are we going to parse this to number
			listTitle: z.string(),
			listButtonDownload: z.boolean(),
			listButtonCart: z.boolean(),
			displayListItemName: z.boolean(),
			ordinal: z.number().int().positive(), // TODO: is this just 0/1?
		}),
	),
});

export const BrandPortalPageBlockRawSchema = BrandPortalPageBlockSchema.omit({
	goo: true,
}).extend({
	goo: z.string(),
});

export const BRAND_PORTAL_PAGE_GOO_KEYS = [
	'template',
	'title',
	'displayTitle',
] as const;

export const BrandPortalPageSchema = z.object({
	id: z.number().int().positive(),
	blocks: z.array(BrandPortalPageBlockSchema),
	goo: stringToJSONSchema.pipe(
		z.object({
			template: z.string(), // TODO: enum
			title: z.string(),
			displayTitle: z.boolean(),
		}),
	),
	/** ex 2019-09-20 16:46:40 */
	modDate: z.string(),
	portalId: z.number().int().positive(),
	slug: z.string(),
	userId: z.number().int().positive(),
});

export const BrandPortalPageRawSchema = BrandPortalPageSchema.extend({
	blocks: z.array(BrandPortalPageBlockRawSchema),
	goo: z.string(),
});

export type IBrandPortalPage = z.infer<typeof BrandPortalPageSchema>;

export type IBrandPortalPageRaw = z.infer<typeof BrandPortalPageRawSchema>;

export type IBrandPortalPageBlock = z.infer<typeof BrandPortalPageBlockSchema>;

export type IBrandPortalPageBlockRaw = z.infer<
	typeof BrandPortalPageBlockRawSchema
>;
